<div class="w-full">
  <!-- DataFrame Column Search -->
  <div
    class="mb-4 mt-4 p-4 border border-gray-200 rounded-lg"
    *ngIf="editable && enablePiPointValidation"
  >
    <h4 class="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
      <i class="pi pi-search mr-2"></i>
      Recherche de points PI
    </h4>

    <div
      *ngIf="isLoadingExistingPiPoints"
      class="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md"
    >
      <div class="flex items-center gap-2 text-sm text-blue-700">
        <i class="pi pi-spin pi-spinner text-blue-600"></i>
        Chargement des points Pi existants pour cette règle...
      </div>
    </div>

    <div class="flex gap-2 mb-3">
      <input
        type="text"
        class="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
        placeholder="Tapez le tag du point Pi (ex: G5003_TC_S34_40040.PV, U4-FC-001.OKPV...)"
        [(ngModel)]="piPointSearchTerm"
        (input)="onPiPointSearchInput($event)"
        style="text-transform: uppercase"
      />
      <button
        [ind4RoleDisabled]="'SED_PLUS_SUPER_USER'"
        type="button"
        class="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        (click)="searchPiPoint()"
        [disabled]="!piPointSearchTerm.trim() || isSearchingPiPoint"
      >
        <i *ngIf="!isSearchingPiPoint" class="pi pi-search mr-1"></i>
        <i *ngIf="isSearchingPiPoint" class="pi pi-spin pi-spinner mr-1"></i>
        {{ isSearchingPiPoint ? "Recherche..." : "Rechercher" }}
      </button>
    </div>

    <!-- Loading state -->
    <div
      *ngIf="isSearchingPiPoint"
      class="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md"
    >
      <div class="flex items-center gap-2 text-sm text-blue-700">
        <i class="pi pi-spin pi-spinner text-blue-600"></i>
        Vérification de la colonne "{{ piPointSearchTerm }}" en cours...
      </div>
    </div>

    <!-- Search Results - Shows only if API returns true -->
    <div
      *ngIf="
        piPointSearchResults.length > 0 && isPiPointValid && !isSearchingPiPoint
      "
      class="mb-3"
    >
      <div class="text-sm text-gray-600 mb-2">Point PI trouvé :</div>
      <div class="flex flex-wrap gap-2">
        <span
          *ngFor="let piPointSearchResult of piPointSearchResults"
          class="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 text-sm rounded-xl cursor-pointer hover:bg-green-200 transition-colors"
          (click)="selectPiPoint(piPointSearchResult)"
        >
          <i class="pi pi-check-circle mr-1"></i>
          {{ piPointSearchResult.tag }}
        </span>
      </div>
    </div>

    <div
      *ngIf="
        piPointSearchPerformed &&
        !isPiPointValid &&
        piPointSearchTerm.trim() &&
        !isSearchingPiPoint
      "
      class="mb-3 p-3 bg-red-50 border border-red-200 rounded-md"
    >
      <div class="flex items-center gap-2 text-sm text-red-700">
        <i class="pi pi-times-circle text-red-600"></i>
        Le point PI "{{ piPointSearchTerm }}" n'existe pas dans le système.
      </div>
    </div>

    <div *ngIf="selectedPiPoints.length > 0" class="mt-3">
      <div class="text-sm text-gray-600 dark:text-gray-200 mb-2">
        Points PI sélectionnés :
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <span
          *ngFor="let piPoint of selectedPiPoints"
          class="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
        >
          {{ piPoint.tag }}
          <button
            type="button"
            class="ml-2 text-blue-600 hover:text-blue-800"
            (click)="removeSelectedPiPoint(piPoint)"
          >
            <i class="pi pi-times"></i>
          </button>
        </span>
        <button
          [ind4RoleDisabled]="'SED_PLUS_SUPER_USER'"
          type="button"
          class="px-4 py-2 bg-red-500 text-white rounded-md text-sm hover:bg-red-600 transition-colors ml-auto"
          (click)="clearAllSelectedPiPoints()"
        >
          <i class="pi pi-trash mr-1"></i>
          Effacer tout
        </button>
      </div>
    </div>
  </div>

  <div
    class="flex items-center gap-2 mb-2"
    *ngIf="validateSyntax && currentCode.trim()"
  >
    <i
      [class]="
        isValidSyntax && piPointValidationErrors.length === 0
          ? 'pi pi-check-circle text-green-500'
          : 'pi pi-times-circle text-red-500'
      "
      [pTooltip]="syntaxError || 'Syntaxe Python valide'"
      tooltipPosition="left"
    ></i>
    <span
      class="text-sm"
      [class.text-green-600]="
        isValidSyntax && piPointValidationErrors.length === 0
      "
      [class.text-red-600]="
        !isValidSyntax || piPointValidationErrors.length > 0
      "
    >
      {{
        isValidSyntax && piPointValidationErrors.length === 0
          ? "Syntaxe et points PI valides"
          : "Erreur de syntaxe ou de point PI"
      }}
    </span>
  </div>

  <!-- Example code indicator -->
  <div
    class="flex items-center gap-2 mb-2 text-blue-600"
    *ngIf="isShowingExample"
  >
    <i class="pi pi-info-circle"></i>
    <span class="text-sm">
      Code d'exemple - Commencez à taper pour le remplacer
    </span>
  </div>

  <!-- Code editor/display area -->
  <div
    class="w-full border border-gray-300 rounded-lg"
    [class.border-red-400]="!isValidSyntax"
    [class.border-green-400]="
      isValidSyntax && validateSyntax && currentCode.trim()
    "
    [class.border-blue-300]="isShowingExample"
    *ngIf="currentCode.length || editable"
  >
    <div class="relative" [style.min-height]="minHeight">
      <!-- Editable mode -->
      <div
        *ngIf="editable"
        class="relative overflow-hidden"
        [style.min-height]="minHeight"
      >
        <!-- Syntax highlighted background -->
        <pre
          #highlightedCodePre
          class="absolute inset-0 m-0 pointer-events-none overflow-auto"
          style="
            background: transparent;
            border: none;
            z-index: 1;
            font-family:
              &quot;Consolas&quot;, &quot;Monaco&quot;,
              &quot;Lucida Console&quot;, &quot;Liberation Mono&quot;,
              &quot;DejaVu Sans Mono&quot;,
              &quot;Bitstream Vera Sans Mono&quot;, &quot;Courier New&quot;,
              monospace;
            font-size: 14px;
            line-height: 1.5;
            tab-size: 4;
            padding: 16px;
            margin: 0;
            white-space: pre;
            word-wrap: normal;
            overflow-wrap: normal;
            box-sizing: border-box;
          "
          [innerHTML]="highlightedCode"
        ></pre>

        <!-- Editable textarea -->
        <textarea
          #codeTextarea
          class="code-textarea absolute inset-0 w-full h-full resize-none border-none outline-none bg-transparent z-10 overflow-auto"
          style="
            color: transparent !important;
            font-family:
              &quot;Consolas&quot;, &quot;Monaco&quot;,
              &quot;Lucida Console&quot;, &quot;Liberation Mono&quot;,
              &quot;DejaVu Sans Mono&quot;,
              &quot;Bitstream Vera Sans Mono&quot;, &quot;Courier New&quot;,
              monospace;
            font-size: 14px;
            line-height: 1.5;
            tab-size: 4;
            padding: 16px;
            margin: 0;
            white-space: pre;
            word-wrap: normal;
            overflow-wrap: normal;
            box-sizing: border-box;
          "
          [value]="currentCode"
          [placeholder]="placeholder"
          [disabled]="disabled"
          (input)="onCodeInput($event)"
          (scroll)="onTextareaScroll()"
          (keydown)="onKeyDown($event)"
          (blur)="onTouched()"
          (paste)="onTextareaPaste($event)"
          (drop)="onTextareaDrop($event)"
          spellcheck="false"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
        ></textarea>
      </div>

      <!-- Non-editable display mode -->
      <div *ngIf="!editable" class="p-4">
        <pre
          class="language-python m-0 whitespace-pre"
          style="
            background: transparent;
            border: none;
            padding: 0;
            font-family:
              &quot;Consolas&quot;, &quot;Monaco&quot;,
              &quot;Lucida Console&quot;, &quot;Liberation Mono&quot;,
              &quot;DejaVu Sans Mono&quot;,
              &quot;Bitstream Vera Sans Mono&quot;, &quot;Courier New&quot;,
              monospace;
            font-size: 14px;
            line-height: 1.5;
            word-wrap: normal;
            white-space: pre;
          "
        ><code [innerHTML]="highlightedCode || 'Aucun code Python défini'"></code></pre>
      </div>
    </div>

    <!-- Error message -->
    <div
      *ngIf="!isValidSyntax && syntaxError"
      class="px-4 py-2 bg-red-50 border-t border-red-200 text-red-700 text-sm"
    >
      <i class="pi pi-exclamation-triangle mr-2"></i>
      {{ syntaxError }}
    </div>
  </div>

  <div
    *ngIf="piPointValidationErrors.length > 0 && !isLoadingExistingPiPoints"
    class="mt-2 mb-2 p-3 bg-red-50 border border-red-200 rounded-md"
  >
    <div class="text-sm font-medium text-red-700 mb-2">
      <i class="pi pi-exclamation-triangle mr-1"></i>
      Erreurs de validation :
    </div>
    <ul class="text-sm text-red-600 list-disc list-inside">
      <li *ngFor="let error of piPointValidationErrors">{{ error }}</li>
    </ul>
  </div>

  <!-- Warning if not all selected PI points are used in ANY code -->
  <div
    *ngIf="
      (unusedSelectedPiPointsGlobal.length || 0) > 0 &&
      !isLoadingExistingPiPoints
    "
    class="mt-2 mb-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md"
  >
    <div class="text-sm font-medium text-yellow-700 mb-2">
      <i class="pi pi-exclamation-triangle mr-1"></i>
      Attention : Certains points PI sélectionnés ne sont pas utilisés dans le
      code.
    </div>
    <ul class="text-sm text-yellow-700 list-disc list-inside">
      <li *ngFor="let piPoint of unusedSelectedPiPointsGlobal">
        {{ piPoint }}
      </li>
    </ul>
  </div>

  <!-- Empty state when no code -->
  <div
    *ngIf="!currentCode.length && !editable"
    class="text-gray-500 text-center py-4 italic"
  >
    Aucun code Python défini
  </div>

  <!-- Code statistics (optional) -->
  <div
    *ngIf="currentCode.trim() && editable"
    class="text-xs text-gray-500 mt-2 flex justify-between"
  >
    <span>{{ currentCode.split("\n").length }} ligne(s)</span>
    <span>{{ currentCode.length }} caractère(s)</span>
  </div>
</div>
